import { Box, Button, Chip, Drawer, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { STATUS_META, BRAND_COLOR, formatLeadDateTime, isReportableStatus } from "../constants";

interface LeadDetailDrawerProps {
  data: LeadsManagementData;
}

export default function LeadDetailDrawer({ data }: LeadDetailDrawerProps) {
  const { detailLead, closeLeadDetail, reportSingleLead, isReporting, canReportLeads } = data;

  if (!detailLead) return null;

  const meta = STATUS_META[detailLead.status];
  const reportable = isReportableStatus(detailLead.status);
  const notReportableMessage =
    detailLead.status === "Not_Supported"
      ? "This lead cannot be reported — brand not supported."
      : detailLead.status === "Success"
      ? "This lead has already been reported successfully."
      : "This lead is not reportable.";

  const fields: Array<[string, string]> = [
    ["Full name:", detailLead.name],
    ["Email:", detailLead.email],
    ["Phone:", detailLead.phone],
    ["Zip code:", detailLead.zip],
    ["Business name:", detailLead.businessName],
    ["Time in business:", detailLead.timeInBusiness],
    ["Monthly revenue:", detailLead.revenue],
    ["Credit rating:", detailLead.credit],
    ["Borrow amount:", detailLead.amount],
    ["Business industry:", detailLead.industry],
    ["Loan purpose:", detailLead.loanPurpose],
    ["User unique ID:", detailLead.userUniqueId],
    ["Traffic source:", detailLead.trafficSource],
    ["Ad ID:", detailLead.adId],
    ["Ad platform click ID:", detailLead.adPlatformClickId],
    ["Brand name:", detailLead.brand],
    ["Date of acquisition:", formatLeadDateTime(detailLead.date)],
    ["Reported at:", formatLeadDateTime(detailLead.reportedAt)],
  ];

  return (
    <Drawer
      anchor="right"
      open
      onClose={closeLeadDetail}
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 200 }}
      PaperProps={{ sx: { width: 452, zIndex: (theme) => theme.zIndex.appBar + 200 } }}
      slotProps={{ backdrop: { onClick: closeLeadDetail } }}
    >
      <Box sx={{ display: "flex", alignItems: "center", p: "20px 24px", borderBottom: "1px solid #ececec" }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 19, fontWeight: 700, color: "#1a1a1a" }}>
            Lead #{detailLead.id}
          </Typography>
          <Chip
            label={meta.label}
            size="small"
            sx={{ mt: 0.5, bgcolor: meta.bg, color: meta.color, fontWeight: 500 }}
          />
        </Box>
        <IconButton onClick={closeLeadDetail}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: "6px 26px 20px" }}>
        {fields.map(([label, value]) => (
          <Box
            key={label}
            sx={{
              display: "grid",
              gridTemplateColumns: "38% 62%",
              gap: 1.5,
              py: 1.85,
              borderBottom: "1px solid #f2f2f2",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 14, color: "#8a8a8a" }}>{label}</Typography>
            <Typography sx={{ fontSize: 14.5, color: "#212121" }}>{value}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: "18px 24px", borderTop: "1px solid #ececec" }}>
        {reportable ? (
          <Button
            fullWidth
            variant="contained"
            startIcon={<SendIcon />}
            disabled={!canReportLeads || isReporting}
            onClick={() => reportSingleLead(detailLead.id)}
            data-tracker="930"
            sx={{ bgcolor: BRAND_COLOR, "&:hover": { bgcolor: "#7B1FA2" }, py: 1.4, letterSpacing: "0.4px" }}
          >
            {isReporting ? "Reporting…" : "Report to brand"}
          </Button>
        ) : (
          <Typography sx={{ textAlign: "center", fontSize: 13.5, color: "#9e9e9e" }}>
            {notReportableMessage}
          </Typography>
        )}
      </Box>
    </Drawer>
  );
}
